import { Mui } from "@osu/react-ui";
import { useMemo, useState } from 'react';
import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import { useGetOpenScholarsProgramsQuery, useAdjustScholarsPreferencesMutation } from '../../services/graphql/api';
import { selectSearchData } from "../../services/graphql/slices/search-slice";
import { generateRandomRankings, randomAlternateOptIn, REQUIRED_COUNT } from "../../util/scholars/simulatePreferences";

// HIDDEN ADMIN / TEST TOOL — "Simulate Preference Submissions" (Advanced Search, Scholars population).
// Gated in ResultsGrid behind the REACT_APP_ENABLE_PREFERENCE_SIMULATOR flag + SCHOLARS-ADMIN. Seeds random
// but VALID ranked preferences for the applicants currently in the search grid, ONE PER APPLICANT.
//
// Uses the admin-on-behalf adjustScholarsPreferences mutation (NOT the student submitScholarsPreferences):
// adjust tolerates BOTH pre-submission states — SCHOLARS_AWAITING_PREFERENCES and
// SCHOLARS_PREFERENCING_REQUESTED — and drives the applicant to SCHOLARS_PREFERENCES_SUBMITTED, whereas the
// student submit path is gated to PREFERENCING_REQUESTED only. For an applicant already IN REVIEW, adjust
// returns success:false with an UPSTREAM warning (it would reset their cascade) UNLESS confirmUpstream is
// passed; the simulator deliberately does NOT force that reset and reports those as a skip.
const DialogScholarsSimulatePreferences = ({ openDialog, setOpenDialog, skip, totalCount }) => {
    const [adjustScholarsPreferences] = useAdjustScholarsPreferencesMutation();
    const { data: programs = [], isFetching: programsLoading } = useGetOpenScholarsProgramsQuery(
        skip || !openDialog ? skipToken : undefined
    );

    const resultData = useSelector(selectSearchData);

    // Applicant ids from the current (filtered) result grid. De-duped; blanks dropped.
    const applicantIds = useMemo(() => {
        const rows = Array.isArray(resultData?.results) ? resultData.results : [];
        const ids = rows.map((r) => r?.applicantEmployeeId).filter(Boolean);
        return [...new Set(ids)];
    }, [resultData?.results]);

    const openProgramIds = useMemo(
        () => (programs || []).map((p) => p?.subProgramId).filter(Boolean),
        [programs]
    );

    const [limit, setLimit] = useState("");           // optional cap on how many applicants to process
    const [randomOptIn, setRandomOptIn] = useState(true);
    const [delayMs, setDelayMs] = useState("0");       // optional per-submission delay (throttle)

    const [isRunning, setIsRunning] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [tally, setTally] = useState({ submitted: 0, skipped: 0, failed: 0 });
    const [failures, setFailures] = useState([]); // { applicantEmployeeId, reason }

    if (skip) { return null; }

    const notEnoughPrograms = !programsLoading && openProgramIds.length < REQUIRED_COUNT;
    const noApplicants = applicantIds.length === 0;

    // Effective target count given the optional limit.
    const parsedLimit = Number.parseInt(limit, 10);
    const targetCount = Number.isFinite(parsedLimit) && parsedLimit > 0
        ? Math.min(parsedLimit, applicantIds.length)
        : applicantIds.length;

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Non-fatal "skip" outcomes (reported separately from real failures):
    //  - already preferenced / past the pre-submission stage (re-run over an overlapping population), or
    //  - applicant is IN REVIEW so adjust would reset their cascade (we don't force confirmUpstream).
    const isSkippable = (text = "") => {
        const t = text.toLowerCase();
        return (
            t.includes("already") ||
            t.includes("not in scholars_preferencing_requested") ||
            t.includes("pre-submission") ||
            t.includes("in review") ||
            t.includes("upstream")
        );
    };

    async function runSimulation() {
        if (isRunning) { return; }
        setIsRunning(true);
        setShowSummary(false);
        setFailures([]);
        setTally({ submitted: 0, skipped: 0, failed: 0 });

        const ids = applicantIds.slice(0, targetCount);
        setProgress({ done: 0, total: ids.length });

        const perCallDelay = Math.max(0, Number.parseInt(delayMs, 10) || 0);
        const runningTally = { submitted: 0, skipped: 0, failed: 0 };
        const runningFailures = [];

        for (let i = 0; i < ids.length; i++) {
            const applicantEmployeeId = ids[i];
            try {
                const rankings = generateRandomRankings(openProgramIds);
                const alternateOptIn = randomOptIn ? randomAlternateOptIn() : true;
                // term intentionally omitted — the backend resolves it from each applicant's Scholars record.
                // confirmUpstream omitted (defaults false): an in-review applicant is reported as a skip
                // rather than having their cascade reset by the simulator.
                const res = await adjustScholarsPreferences({ applicantEmployeeId, rankings, alternateOptIn }).unwrap();
                if (res?.success === false) {
                    // UPSTREAM (in review) — adjust declined without confirmUpstream. Treat as a skip.
                    runningTally.skipped += 1;
                } else {
                    runningTally.submitted += 1;
                }
            } catch (err) {
                const reason = err?.message ?? err?.error ?? "Unknown error";
                if (isSkippable(`${err?.name ?? ""} ${reason}`)) {
                    runningTally.skipped += 1;
                } else {
                    runningTally.failed += 1;
                    runningFailures.push({ applicantEmployeeId, reason });
                }
            }
            setProgress({ done: i + 1, total: ids.length });
            setTally({ ...runningTally });
            if (perCallDelay > 0 && i < ids.length - 1) { await delay(perCallDelay); }
        }

        setFailures(runningFailures);
        setShowSummary(true);
        setIsRunning(false);
    }

    // Deliberately do NOT clear the Advanced Search criteria/results on close (parity with the other
    // Scholars bulk dialogs) — the admin keeps the filtered grid after running the simulation.
    const handleClose = () => {
        if (isRunning) { return; }
        setOpenDialog(false);
        setShowSummary(false);
        setProgress({ done: 0, total: 0 });
        setTally({ submitted: 0, skipped: 0, failed: 0 });
        setFailures([]);
        setLimit("");
        setDelayMs("0");
        setRandomOptIn(true);
    };

    const handleDialogClose = (event, reason) => {
        if (isRunning) { return; }
        if (showSummary && reason === 'backdropClick') { return; }
        handleClose();
    };

    const canRun = !isRunning && !programsLoading && !notEnoughPrograms && !noApplicants && targetCount > 0;

    return (
        <Mui.Dialog
            open={openDialog}
            onClose={handleDialogClose}
            aria-labelledby="scholars-sim-prefs-dialog-title"
            aria-describedby="scholars-sim-prefs-dialog-description"
            fullWidth
            maxWidth="sm">
            <Mui.DialogTitle id="scholars-sim-prefs-dialog-title">
                Simulate Preference Submissions
            </Mui.DialogTitle>

            <Mui.Alert severity="warning" sx={{ mx: 2 }}>
                Test tool. This submits <strong>real</strong> Scholars preferences on behalf of the applicants
                in the current search results. Applicants who are awaiting preferences or have preferencing
                requested will be submitted; those already preferenced or in review are skipped.
            </Mui.Alert>

            <Mui.DialogContent>
                {!showSummary && (
                    <Mui.DialogContentText id="scholars-sim-prefs-dialog-description" sx={{ mb: 2 }}>
                        Generates random, valid ranked preferences (ranks 1–3 required) from the open Scholars
                        programs and submits them for each applicant in the filtered population. Term is resolved
                        per applicant on the server.
                    </Mui.DialogContentText>
                )}

                {programsLoading ? (
                    <Mui.Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Mui.CircularProgress />
                    </Mui.Box>
                ) : (
                    <>
                        {notEnoughPrograms && (
                            <Mui.Alert severity="error" sx={{ mb: 2 }}>
                                Need at least {REQUIRED_COUNT} open Scholars programs to generate valid
                                preferences (found {openProgramIds.length}).
                            </Mui.Alert>
                        )}
                        {noApplicants && (
                            <Mui.Alert severity="info" sx={{ mb: 2 }}>
                                No applicants in the current search results.
                            </Mui.Alert>
                        )}

                        {!showSummary && !notEnoughPrograms && !noApplicants && (
                            <Mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Mui.Typography variant="body2" color="text.secondary">
                                    Population in grid: <strong>{applicantIds.length}</strong>
                                    {typeof totalCount === 'number' && totalCount !== applicantIds.length
                                        ? ` (of ${totalCount} total matched)`
                                        : ""}
                                    . Open programs: <strong>{openProgramIds.length}</strong>.
                                </Mui.Typography>

                                <Mui.TextField
                                    type="number"
                                    label="Limit (optional)"
                                    helperText={`Blank = all ${applicantIds.length}. Will process ${targetCount}.`}
                                    value={limit}
                                    onChange={(e) => setLimit(e.target.value)}
                                    disabled={isRunning}
                                    inputProps={{ min: 1, max: applicantIds.length, 'data-testid': 'sim-limit' }}
                                    fullWidth
                                />

                                <Mui.TextField
                                    type="number"
                                    label="Delay between submissions (ms)"
                                    helperText="Optional throttle to avoid hammering the API."
                                    value={delayMs}
                                    onChange={(e) => setDelayMs(e.target.value)}
                                    disabled={isRunning}
                                    inputProps={{ min: 0, step: 50, 'data-testid': 'sim-delay' }}
                                    fullWidth
                                />

                                <Mui.FormControlLabel
                                    control={
                                        <Mui.Checkbox
                                            checked={randomOptIn}
                                            onChange={(e) => setRandomOptIn(e.target.checked)}
                                            disabled={isRunning}
                                            inputProps={{ 'data-testid': 'sim-random-optin' }}
                                        />
                                    }
                                    label="Randomize alternate opt-in (unchecked = always opt in)"
                                />
                            </Mui.Box>
                        )}

                        {(isRunning || showSummary) && (
                            <Mui.Box sx={{ mt: 2 }}>
                                <Mui.Typography variant="body2" sx={{ mb: 1 }}>
                                    Progress: {progress.done} / {progress.total}
                                </Mui.Typography>
                                <Mui.LinearProgress
                                    variant="determinate"
                                    value={progress.total ? (progress.done / progress.total) * 100 : 0}
                                    sx={{ mb: 2 }}
                                />
                                <Mui.Box aria-live="polite">
                                    <Mui.Typography variant="body2" color="success.main">
                                        Submitted: {tally.submitted}
                                    </Mui.Typography>
                                    <Mui.Typography variant="body2" color="text.secondary">
                                        Skipped (already preferenced or in review): {tally.skipped}
                                    </Mui.Typography>
                                    <Mui.Typography variant="body2" color="error.main">
                                        Failed: {tally.failed}
                                    </Mui.Typography>
                                </Mui.Box>

                                {showSummary && failures.length > 0 && (
                                    <Mui.Box sx={{ mt: 2, maxHeight: 160, overflow: 'auto' }}>
                                        <Mui.Typography variant="caption" color="error.main">
                                            Failures:
                                        </Mui.Typography>
                                        {failures.map((f) => (
                                            <Mui.Typography key={f.applicantEmployeeId} variant="caption" display="block" color="text.secondary">
                                                {f.applicantEmployeeId}: {f.reason}
                                            </Mui.Typography>
                                        ))}
                                    </Mui.Box>
                                )}
                            </Mui.Box>
                        )}
                    </>
                )}
            </Mui.DialogContent>

            <Mui.DialogActions className="padding-2">
                {showSummary ? (
                    <Mui.Button color="primary" style={{ border: "none" }} variant="contained" onClick={handleClose}>
                        Close
                    </Mui.Button>
                ) : (
                    <>
                        <Mui.Button
                            color="secondary"
                            style={{ border: "none" }}
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isRunning}
                        >
                            Cancel
                        </Mui.Button>
                        <Mui.Button
                            color="primary"
                            style={{ border: "none" }}
                            variant="contained"
                            onClick={runSimulation}
                            disabled={!canRun}
                            data-testid="sim-run"
                        >
                            {isRunning && <Mui.CircularProgress size={20} style={{ marginRight: '.5rem' }} />}
                            {isRunning ? "Running…" : `Simulate ${targetCount} Submission${targetCount === 1 ? "" : "s"}`}
                        </Mui.Button>
                    </>
                )}
            </Mui.DialogActions>
        </Mui.Dialog>
    );
};

export default DialogScholarsSimulatePreferences;
